import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, ShoppingCart, Store, Truck, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import OptionSheet from '@/components/OptionSheet';
import ConsumerInquiryModal from '@/components/ConsumerInquiryModal';
import ProductDetailRenderer from '@/components/product/ProductDetailRenderer';
import ProductSizeTable from '@/components/product/ProductSizeTable';
import { useProductLiveVideo, LiveVideoThumb, LiveVideoPreview, LiveVideoHost } from '@/components/product/ProductLiveVideo';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const SHIPPING_LABEL = { free: '무료배송', paid: '유료배송', conditional: '조건부 무료배송' };
const SHIPPING_OPTION_LABEL = { seller_default: '판매자 배송설정', free: '무료배송', custom: '별도배송' };

/**
 * Public product detail page — /product/:productId
 *
 * PC layout: 2-column (gallery left, buy panel right sticky).
 * Mobile:   stacked gallery + info, buy actions pinned to the bottom.
 * Option selection + quantity moved into a bottom sheet (mobile) / centred
 * modal (desktop). The PDP itself now only displays static product info and
 * exposes two triggers: 장바구니 / 주문하기 — both open the same sheet with
 * a slightly different primary-CTA emphasis.
 */
const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [sheet, setSheet] = useState(null); // null | 'cart' | 'buy'
  const [askOpen, setAskOpen] = useState(false);
  const lv = useProductLiveVideo(productId);

  // Prod-S4 · Shorts attribution 파라미터 파싱 · 일반 진입에는 영향 없음
  const attribution = useMemo(() => {
    const from = searchParams.get('from');
    const clipId = searchParams.get('clip_id');
    if (from !== 'clip' || !clipId) return null;
    return {
      source_type:       'shorts',
      source_clip_id:    clipId,
      source_platform:   searchParams.get('platform') || null,
      source_product_id: searchParams.get('src_pid') || productId,
    };
  }, [searchParams, productId]);

  // /product/{pid}?buy=1 → 초기 진입 시 주문 sheet 자동 오픈 (Shorts 바로구매 UX)
  useEffect(() => {
    if (searchParams.get('buy') === '1' && product) setSheet('buy');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/products/${productId}`);
        if (!cancelled) setProduct(res.data);
      } catch { if (!cancelled) setNotFound(true); }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const arr = [];
    if (product.image) arr.push(product.image);
    (product.additional_images || []).forEach((u) => { if (u && !arr.includes(u)) arr.push(u); });
    return arr;
  }, [product]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="product-detail-notfound">
        <p className="text-2xl font-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>상품을 찾을 수 없습니다</p>
        <Link to="/" className="text-sm text-[#FF3B30] font-bold hover:underline">홈으로 돌아가기</Link>
      </div>
    );
  }
  if (!product) return <div className="max-w-3xl mx-auto px-4 py-24 text-sm text-gray-400 text-center">불러오는 중…</div>;

  const openSheet = (intent) => setSheet(intent);
  const optionCount = (product.options || []).length;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-24 sm:pb-8" data-testid="product-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white mb-4" data-testid="product-back">
          <ArrowLeft size={14} /> 뒤로
        </button>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6 lg:gap-10">
          {/* Gallery */}
          <div>
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 mb-3" data-testid="product-main-image">
              {gallery.length > 0 ? (
                <SafeImg src={gallery[activeImageIdx]} kind="product" alt={product.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400"><Package size={36} /></div>
              )}
              {gallery.length > 1 && (
                <>
                  <button type="button" onClick={() => setActiveImageIdx((i) => (i - 1 + gallery.length) % gallery.length)} className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/80 dark:bg-black/60 rounded-full p-1.5 shadow" aria-label="이전 이미지" data-testid="gallery-prev"><ChevronLeft size={16} /></button>
                  <button type="button" onClick={() => setActiveImageIdx((i) => (i + 1) % gallery.length)} className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/80 dark:bg-black/60 rounded-full p-1.5 shadow" aria-label="다음 이미지" data-testid="gallery-next"><ChevronRight size={16} /></button>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="product-thumbnails">
                {gallery.map((url, i) => (
                  <button key={i} type="button" onClick={() => setActiveImageIdx(i)} className={`shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 bg-gray-100 dark:bg-gray-800 ${i === activeImageIdx ? 'border-[#FF3B30]' : 'border-transparent'}`} aria-label={`${i + 1}번 이미지`}>
                    <SafeImg src={url} kind="product" alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
            {lv.meta && <LiveVideoThumb meta={lv.meta} image={gallery[0]} onOpen={() => lv.open(product)} />}
          </div>

          {/* Info & Buy panel */}
          <div className="lg:sticky lg:top-6 space-y-4 self-start">
            <div>
              <Link to={`/seller/${product.seller_id}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-[#FF3B30]" data-testid="product-seller-link">
                <Store size={12} /> {product.seller?.name || '판매자'}
              </Link>
              {product.brand && <span className="ml-2 text-[10px] font-black tracking-widest uppercase text-gray-400">{product.brand}</span>}
              <h1 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="product-name">{product.name}</h1>
            </div>

            <div className="flex items-baseline gap-2">
              {product.discount_rate > 0 && (
                <>
                  <span className="text-2xl font-black text-[#FF3B30]" data-testid="product-discount">{product.discount_rate}%</span>
                  <span className="text-sm text-gray-400 line-through">{won(product.original_price)}</span>
                </>
              )}
              <span className="ml-auto text-2xl font-black" data-testid="product-price">{won(product.sale_price)}</span>
            </div>

            {optionCount > 0 && (
              <p className="text-xs text-gray-500" data-testid="product-options-hint">
                옵션 {optionCount}종 · 아래 <strong className="text-black dark:text-white">장바구니</strong> 또는 <strong className="text-black dark:text-white">주문하기</strong>를 눌러 옵션을 선택하세요.
              </p>
            )}

            <div className="rounded-xl bg-black/[0.02] dark:bg-white/[0.03] p-3 text-xs text-gray-600 dark:text-gray-400 space-y-1" data-testid="shipping-summary">
              <p className="flex items-center gap-1.5"><Truck size={12} className="text-[#FF3B30]" /> {SHIPPING_OPTION_LABEL[product.shipping_option] || '판매자 배송설정'} · {SHIPPING_LABEL[product.shipping_policy?.shipping_type] || '유료배송'}</p>
              {product.shipping_policy?.shipping_type !== 'free' && product.shipping_option !== 'free' && (
                <p>기본 배송비 {won(product.shipping_policy?.base_fee)}{product.shipping_policy?.shipping_type === 'conditional' && ` · ${won(product.shipping_policy?.free_threshold)} 이상 무료`}</p>
              )}
              <p>제주 +{won(product.shipping_policy?.jeju_extra)} · 도서산간 +{won(product.shipping_policy?.remote_extra)}</p>
            </div>

            {/* Desktop buy panel (right column) */}
            <div className="hidden lg:flex gap-2 pt-2 border-t border-black/5 dark:border-white/10">
              <button type="button" onClick={() => openSheet('cart')} className="flex-1 py-3 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold hover:border-[#FF3B30] hover:text-[#FF3B30]" data-testid="add-to-cart-desktop"><ShoppingCart size={14} className="inline -mt-0.5 mr-1" /> 장바구니</button>
              <button type="button" onClick={() => openSheet('buy')} className="flex-1 py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold" data-testid="buy-now-desktop"><ShoppingBag size={14} className="inline -mt-0.5 mr-1" /> 주문하기</button>
            </div>

            <button type="button" onClick={() => setAskOpen(true)}
              className="w-full py-2 mt-2 rounded-full text-xs font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/5"
              data-testid="product-ask-inquiry">
              💬 상품 문의하기
            </button>
          </div>
        </div>

        {/* STEP PRODUCT-DETAIL-SIZE-TABLE-1 · 상품 상세 설명 바로 위에 사이즈 안내.
            seller_products.size_table 가 비어있으면 ProductSizeTable 이 null 을 반환
            하여 기존 상품은 완전히 동일하게 표시된다 (backward compat). */}
        {lv.meta && <LiveVideoPreview meta={lv.meta} image={gallery[0]} onOpen={() => lv.open(product)} />}

        <ProductSizeTable sizeTable={product.size_table} />

        {(product.detail_blocks?.length > 0 || product.description) && (
          <section className="mt-10">
            <h2 className="text-sm font-black tracking-widest uppercase text-gray-500 mb-3">상품 설명</h2>
            <ProductDetailRenderer
              blocks={product.detail_blocks || []}
              legacyDescription={product.description || ''}
            />
          </section>
        )}
      </div>

      {/* Mobile bottom buy bar — no total shown; the sheet computes it after option selection */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white dark:bg-[#0A0A0A] border-t border-black/10 dark:border-white/10 px-4 py-3 flex items-center gap-2" data-testid="mobile-buy-bar">
        <button type="button" onClick={() => openSheet('cart')} className="flex-1 py-3 rounded-full border border-black/15 dark:border-white/15 text-sm font-bold" data-testid="add-to-cart-mobile"><ShoppingCart size={14} className="inline -mt-0.5 mr-1" /> 장바구니</button>
        <button type="button" onClick={() => openSheet('buy')} className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-sm font-bold" data-testid="buy-now-mobile"><ShoppingBag size={14} className="inline -mt-0.5 mr-1" /> 주문하기</button>
      </div>

      <LiveVideoHost item={lv.item} onClose={lv.close} onBuy={() => openSheet('buy')} />
      <OptionSheet product={product} open={!!sheet} intent={sheet || 'cart'} onClose={() => setSheet(null)} attribution={attribution} />
      <ConsumerInquiryModal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        scope="product"
        context={{ product_id: product.product_id, title: product.name }}
        defaultCategory="product"
      />
    </div>
  );
};

export default ProductDetail;
